/**
 * Component Registry for Weidner Apartment Homes Theme
 * 
 * Extends the base preferred theme registry with Weidner Apartment Homes specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-preferred/registry';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { FloorplansList } from './components/FloorplansList';
import { ReviewsCallout } from './components/ReviewsCallout';
import { PageHero } from './components/PageHero';
import { Footer } from './components/Footer';
import { HyperlocalHero } from './components/HyperlocalHero';
import { HyperlocalPageCopy } from './components/HyperlocalPageCopy';
import { SitemapContent } from './components/SitemapContent';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';
import { Gallery } from './components/Gallery';
import { PropertyOverview } from './components/PropertyOverview';
import { NeighborhoodCallout } from './components/NeighborhoodCallout';
import { NeighborhoodMap } from './components/NeighborhoodMap';
import { AccessibilityStatement } from './components/AccessibilityStatement';
import { ContactForm } from './components/ContactForm';
import { ContactUs } from './components/ContactUs';
import { CortLeadFormCta } from './components/CortLeadFormCta';
import { Deals } from './components/Deals';
import { PageBlurb } from './components/PageBlurb';
import { PricingVerbiage } from './components/PricingVerbiage';
import { QualifyingCriteria } from './components/QualifyingCriteria';
import { DallasMavericksGames } from './components/DallasMavericksGames';
import {
  AccessibilityHero,
  AmenitiesHero,
  ApplyNowHero,
  ContactHero,
  FloorPlansHero,
  GalleryHero,
  InteractiveMapHero,
  NeighborhoodHero,
  PetPolicyHero,
  PrivacyPolicyHero,
  ResidentAccessHero,
  ReviewsHero,
  ScheduleATourHero,
  SitemapHero,
  ThankYouHero,
  VirtualTourHero,
} from './components/InteriorHeroes';

/**
 * Weidner Apartment Homes specific component overrides.
 * These components have been customized for the Weidner Apartment Homes brand.
 */
const weidnerapartmenthomesOverrides: Partial<ComponentRegistry> = {
  Header,
  Hero,
  SpecialOfferBanner,
  FloorplansList,
  ReviewsCallout,
  PageHero,
  FloorPlansHero,
  AmenitiesHero,
  GalleryHero,
  NeighborhoodHero,
  ContactHero,
  VirtualTourHero,
  PetPolicyHero,
  ScheduleATourHero,
  InteractiveMapHero,
  PrivacyPolicyHero,
  SitemapHero,
  AccessibilityHero,
  ApplyNowHero,
  ThankYouHero,
  ReviewsHero,
  ResidentAccessHero,
  Footer,
  HyperlocalHero,
  HyperlocalPageCopy,
  SitemapContent,
  VirtualTourEmbed,
  Gallery,
  PropertyOverview,
  NeighborhoodCallout,
  NeighborhoodMap,
  AccessibilityStatement,
  ContactForm,
  ContactUs,
  CortLeadFormCta,
  Deals,
  PageBlurb,
  PricingVerbiage,
  QualifyingCriteria,
  DallasMavericksGames,
};

/**
 * Final merged registry for Weidner Apartment Homes properties.
 * Inherits all components from the preferred theme, with Weidner Apartment Homes overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...weidnerapartmenthomesOverrides,
};

export default registry;
